import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import router from "../router/index.js";

Vue.use(Vuex);

import modSeguridad from "./modSeguridad/index";
import axios from "axios";
import apiBaseUrl from "../services/apiBaseUrl";

axios.defaults.withCredentials = true;

const baseServer = apiBaseUrl();

axios.defaults.baseURL = baseServer;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

let handlingSessionExpired = false;

function handleSessionExpired() {
  if (handlingSessionExpired) {
    return;
  }
  handlingSessionExpired = true;

  localStorage.removeItem("vuex");
  window.location.replace("/LoginForm");
}

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Manejar el error aquí
    if (error.response) {
      // La solicitud fue realizada, pero el servidor respondió con un código de estado fuera del rango 2xx

      const status = error.response.status;

      // 419 es el "Page Expired" de Laravel: el token CSRF ya no coincide
      // porque la sesion del servidor murio. Para el usuario es lo mismo que
      // un 401 y debe llevarlo al login igual.
      if (status === 401 || status === 419) {
        let excludedRoutes = [
          "LoginForm",
          "ForgotPassword",
          "RegisterForm",
          "Registro",
          "ResetPassword",
        ];

        let currentPath = router.currentRoute.path;

        if (!currentPath) {
          currentPath = "/";
          excludedRoutes.push("/");
        }

        if (currentPath == "/") {
          excludedRoutes.push("/");
        } else {
          excludedRoutes = excludedRoutes.filter((r) => r !== "/");
        }

        if (!excludedRoutes.some((ruta) => currentPath.includes(ruta))) {
          handleSessionExpired();
        }
      }
    } else if (error.request) {
      // La solicitud fue realizada pero no se recibió respuesta
      //console.error('Request:', error.request);
    } else {
      // Algo sucedió en la configuración de la solicitud que desencadenó un error
      //console.error('Error:', error.message);
    }
    //console.error('Config:', error.config);

    // Retornar una promesa rechazada
    return Promise.reject(error);
  }
);

export default new Vuex.Store({
  plugins: [createPersistedState()],
  state: {
    user: null,
    errors: null,
    mensaje_login: null,
    auth: false,
    permissions: [],
    menus: [],
    routes: [],
    accessToken: "",
  },
  getters: {
    isAuthenticated: (state) => {
      return state.auth;
    },
    getPermissions: (state) => {
      return state.permissions;
    },
    getRoutes: (state) => {
      return state.routes;
    },
    getMenus: (state) => {
      return state.menus;
    },
    getUser: (state) => {
      return state.user;
    },
    getErrors: (state) => {
      return state.errors;
    },
    getMensajeLogin: (state) => {
      return state.mensaje_login;
    },

    can: (state) => (permissionName) => {
      return (
        state.permissions.find((per) => per.name === permissionName) !==
        undefined
      );
    },
  },
  mutations: {
    setAccessToken(state, token) {
      state.accessToken = token;
    },
    SET_USER(state, user) {
      state.user = user;
      state.auth = Boolean(user);
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },
    SET_MENUS(state, menus) {
      state.menus = menus;
    },
    SET_ROUTES(state, routes) {
      state.routes = routes;
    },
    SET_ERRORS(state, error) {
      state.errors = error;
    },
    SET_MENSAJE_LOGIN(state, mensaje) {
      state.mensaje_login = mensaje;
    },
  },
  actions: {
    async login({ dispatch, commit }, credentials) {
      await axios.get("/sanctum/csrf-cookie");
      await axios
        .post("/login", credentials)
        .then(async (res) => {
          await dispatch("getUser");
          await dispatch("getPermissions");
          await dispatch("getMenus");
          await dispatch("getRoutes", this.getters.getMenus);
          await dispatch("setRoutes");
        })
        .catch(async (error) => {
          const errors = error?.response?.data?.errors ?? {};

          commit("SET_ERRORS", null);
          commit("SET_MENSAJE_LOGIN", null);

          let err_pss_date = errors.password_updated_at;
          //let err_recaptcha = res.response.data.errors.recaptcha;
          //let err_status = res.response.data.errors.status;
          let err_email = errors.email;
          if (typeof err_pss_date !== "undefined") {
            let errors = "password_updated_at";
            commit("SET_ERRORS", errors);
          }
          /*if (typeof err_recaptcha !== "undefined") {
            let errors = "recaptcha";
            commit("SET_ERRORS", errors);
          }
          if (typeof err_status !== "undefined") {
            let errors = "status";
            commit("SET_ERRORS", errors);
          } */
          if (typeof err_email !== "undefined") {
            let errors = "email";
            let mensaje =
              "Los datos ingresados no coinciden, por favor comuníquese con un administrador";
            commit("SET_ERRORS", errors);
            commit("SET_MENSAJE_LOGIN", mensaje);
          }
        });
    },
    async getUser({ commit }) {
      await axios
        .get("/api/user")
        .then((res) => {
          commit("SET_USER", res.data);
        })
        .catch(() => {
          commit("SET_USER", null);
        });
    },

    async logout({ commit }) {
      // La peticion puede fallar sin que eso deba impedir el cierre de sesion:
      // si la sesion del servidor ya expiro, /logout responde 419 (el token
      // CSRF no coincide). Dejando propagar ese error, quien llama nunca
      // alcanzaba la navegacion al login y el boton "Salir" parecia muerto.
      try {
        await axios.post("/logout");
      } catch (e) {
        // La sesion del servidor ya no existe o no se pudo cerrar; el estado
        // local se limpia de todos modos.
      }

      // Limpieza local explicita en vez de releer /api/user, /api/menu y
      // /api/user/permissions: esas tres peticiones respondian 401 y solo
      // servian para dejar el estado vacio por la via del catch.
      commit("SET_USER", null);
      commit("SET_PERMISSIONS", []);
      commit("SET_MENUS", []);
      commit("SET_ROUTES", []);
      localStorage.removeItem("vuex");
    },

    async getPermissions({ commit }) {
      await axios
        .get("/api/user/permissions")
        .then((res) => {
          commit("SET_PERMISSIONS", res.data);
        })
        .catch(() => {
          commit("SET_PERMISSIONS", []);
        });
    },

    async getMenus({ commit }) {
      await axios
        .get("/api/menu")
        .then((response) => {
          commit("SET_MENUS", response.data);
        })
        .catch(() => {
          commit("SET_MENUS", []);
        });
    },

    async getRoutes({ commit }, menus) {
      var r = [];
      menus.forEach((menu) => {
        menu.nivel2.forEach((nivel2) => {
          nivel2.rutas.forEach((ruta) => {
            r.push(ruta);
          });
        });
      });
      /* console.log(r); */
      commit("SET_ROUTES", r);
    },

    async setRoutes() {
      this.getters.getRoutes.forEach((dataRoutes) => {
        var newRoute = {
          path: dataRoutes.path,
          component: () => import("../views/" + dataRoutes.component + ".vue"),
          name: dataRoutes.name,
        };
        router.addRoutes([newRoute]);
      });
    },
  },
  modules: {
    modSeguridad: modSeguridad,
  },
});

